<template>
  <Institutelayout>
    <div
      class="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4"
    >
      <div class="container mx-auto max-w-6xl">
        <!-- Form Section - Full Width -->
        <div
          class="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-6"
        >
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">
              Navbar Configuration
            </h2>
          </div>

          <div class="p-6">
            <form @submit.prevent="saveChanges" class="space-y-6">
              <!-- Logo and Navigation Links Section - Side by Side -->
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Logo Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3
                    class="text-lg font-semibold text-gray-800 mb-3 flex items-center"
                  >
                    <svg
                      class="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      ></path>
                    </svg>
                    Add Your Logo
                  </h3>

                  <div class="relative">
                    <input
                      type="file"
                      id="logo"
                      @change="handleLogoUpload"
                      accept="image/*"
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                    <div
                      class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors duration-200 bg-white"
                    >
                      <svg
                        class="w-10 h-10 text-gray-400 mx-auto mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        ></path>
                      </svg>
                      <p class="text-gray-600 font-medium text-sm">
                        {{ form.logoName || "Click to upload your logo" }}
                      </p>
                      <p class="text-xs text-gray-500 mt-1">
                        PNG, JPG, SVG up to 5MB
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Navigation Links Section -->
                <div class="bg-gray-50 rounded-xl p-4">
                  <h3
                    class="text-lg font-semibold text-gray-800 mb-3 flex items-center"
                  >
                    <svg
                      class="w-5 h-5 mr-2 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      ></path>
                    </svg>
                    Navigation Links
                  </h3>

                  <div class="space-y-3 max-h-64 overflow-y-auto">
                    <div
                      v-for="(link, index) in form.navLinks"
                      :key="index"
                      class="bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
                    >
                      <!-- Main Navigation Link -->
                      <div class="flex items-center space-x-2 mb-2">
                        <div class="flex-1">
                          <input
                            type="text"
                            v-model="link.text"
                            placeholder="Main Link Text"
                            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                          />
                        </div>
                        <div class="flex-1">
                          <input
                            type="url"
                            v-model="link.url"
                            placeholder="https://example.com"
                            class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                          />
                        </div>
                        <button
                          type="button"
                          @click="toggleSubLinks(index)"
                          class="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors duration-200"
                          :class="{ 'bg-blue-50': link.showSubLinks }"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </button>
                        <button
                          type="button"
                          @click="removeNavLink(index)"
                          class="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                        >
                          <svg
                            class="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                          </svg>
                        </button>
                      </div>

                      <!-- Sub Navigation Links -->
                      <div
                        v-if="link.showSubLinks"
                        class="ml-4 space-y-2 border-l-2 border-blue-200 pl-3"
                      >
                        <div class="text-xs font-medium text-gray-600 mb-1">
                          Sub-navigation links:
                        </div>
                        <div
                          v-for="(subLink, subIndex) in link.subLinks"
                          :key="subIndex"
                          class="flex items-center space-x-2"
                        >
                          <div class="flex-1">
                            <input
                              type="text"
                              v-model="subLink.text"
                              placeholder="Sub Link Text"
                              class="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                            />
                          </div>
                          <div class="flex-1">
                            <input
                              type="url"
                              v-model="subLink.url"
                              placeholder="https://example.com"
                              class="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
                            />
                          </div>
                          <button
                            type="button"
                            @click="removeSubLink(index, subIndex)"
                            class="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors duration-200"
                          >
                            <svg
                              class="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M6 18L18 6M6 6l12 12"
                              ></path>
                            </svg>
                          </button>
                        </div>
                        <button
                          type="button"
                          @click="addSubLink(index)"
                          class="w-full py-1.5 px-2 border border-dashed border-gray-300 rounded-md text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-1 text-xs"
                        >
                          <svg
                            class="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            ></path>
                          </svg>
                          <span>Add Sub Link</span>
                        </button>
                      </div>
                    </div>

                    <button
                      type="button"
                      @click="addNavLink"
                      class="w-full py-2 px-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center space-x-2 text-sm"
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        ></path>
                      </svg>
                      <span>Add Navigation Link</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Design Customization Section -->
              <div class="bg-gray-50 rounded-xl p-4">
                <h3
                  class="text-lg font-semibold text-gray-800 mb-3 flex items-center"
                >
                  <svg
                    class="w-5 h-5 mr-2 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z"
                    ></path>
                  </svg>
                  Design Customization
                </h3>

                <div
                  class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                >
                  <div>
                    <label
                      for="navbarBgColor"
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Navbar Background
                    </label>
                    <div class="flex items-center space-x-2">
                      <input
                        type="color"
                        id="navbarBgColor"
                        v-model="form.navbarBgColor"
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{
                        form.navbarBgColor
                      }}</span>
                    </div>
                  </div>

                  <div>
                    <label
                      for="textColor"
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Text Color
                    </label>
                    <div class="flex items-center space-x-2">
                      <input
                        type="color"
                        id="textColor"
                        v-model="form.textColor"
                        class="w-10 h-10 border border-gray-200 rounded-lg cursor-pointer shadow-sm"
                      />
                      <span class="text-xs text-gray-600 font-mono">{{
                        form.textColor
                      }}</span>
                    </div>
                  </div>

                  <div>
                    <label
                      for="fontFamily"
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Font Family
                    </label>
                    <select
                      id="fontFamily"
                      v-model="form.fontFamily"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="sans-serif">Sans-serif</option>
                      <option value="serif">Serif</option>
                      <option value="monospace">Monospace</option>
                      <option value="cursive">Cursive</option>
                      <option value="fantasy">Fantasy</option>
                    </select>
                  </div>

                  <div>
                    <label
                      for="textSize"
                      class="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Text Size
                    </label>
                    <select
                      id="textSize"
                      v-model="form.textSize"
                      class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-white shadow-sm"
                    >
                      <option value="text-sm">Small</option>
                      <option value="text-base">Medium</option>
                      <option value="text-lg">Large</option>
                      <option value="text-xl">Extra Large</option>
                    </select>
                  </div>
                </div>

                <div class="mt-4">
                  <label class="block text-sm font-semibold text-gray-700 mb-2">
                    Logo Position
                  </label>
                  <div
                    class="flex items-center space-x-4 bg-white p-3 rounded-lg border border-gray-200"
                  >
                    <label class="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        v-model="form.logoPosition"
                        value="left"
                        class="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span class="ml-2 text-gray-700 text-sm">Left</span>
                    </label>
                    <label class="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        v-model="form.logoPosition"
                        value="center"
                        class="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span class="ml-2 text-gray-700 text-sm">Center</span>
                    </label>
                    <label class="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        v-model="form.logoPosition"
                        value="right"
                        class="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span class="ml-2 text-gray-700 text-sm">Right</span>
                    </label>
                    <label class="inline-flex items-center cursor-pointer">
                      <input
                        type="radio"
                        v-model="form.logoPosition"
                        value="space-between"
                        class="form-radio h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span class="ml-2 text-gray-700 text-sm"
                        >Space Between</span
                      >
                    </label>
                  </div>
                </div>
              </div>

              <!-- Save Button -->
              <div class="flex justify-end pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  class="px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <span class="flex items-center space-x-2">
                    <svg
                      class="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12"
                      ></path>
                    </svg>
                    <span>Save Changes</span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Preview Section - Below Form -->
        <div
          class="bg-white hidden md:flex flex-col overflow-hidden rounded-2xl shadow-xl border border-gray-100"
        >
          <div class="bg-blue-500 px-8 py-4">
            <h2 class="text-xl font-semibold text-white">Live Preview</h2>
          </div>

          <div class="p-6">
            <!-- Preview Navbar -->
            <div class="border border-gray-200 rounded-xl shadow-lg">
              <nav
                class="px-6 py-4 flex items-center"
                :style="{
                  backgroundColor: form.navbarBgColor,
                  fontFamily: form.fontFamily,
                }"
                :class="{
                  'justify-start': form.logoPosition === 'left',
                  'justify-center': form.logoPosition === 'center',
                  'justify-end': form.logoPosition === 'right',
                  'justify-between': form.logoPosition === 'space-between',
                }"
              >
                <!-- Logo Section -->
                <div class="flex items-center space-x-4">
                  <div
                    class="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center"
                  >
                    <img
                      v-if="form.logoUrl"
                      :src="form.logoUrl"
                      alt="Logo"
                      class="w-full h-full object-contain"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="30"
                      height="30"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                </div>

                <!-- Navigation Links -->
                <div
                  class="flex space-x-6 relative"
                  :class="{
                    'ml-8': form.logoPosition === 'left',
                    'ml-8': form.logoPosition === 'center',
                    'mr-8': form.logoPosition === 'right',
                  }"
                >
                  <div
                    v-for="(link, index) in form.navLinks"
                    :key="index"
                    class="relative group"
                  >
                    <a
                      :href="link.url || '#'"
                      class="font-medium hover:opacity-80 transition-opacity duration-200 flex items-center space-x-1"
                      :class="form.textSize"
                      :style="{
                        color: form.textColor,
                        fontFamily: form.fontFamily,
                      }"
                    >
                      <span>{{ link.text || `Link ${index + 1}` }}</span>
                      <svg
                        v-if="link.subLinks && link.subLinks.length > 0"
                        class="w-4 h-4 transition-transform group-hover:rotate-180"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 9l-7 7-7-7"
                        ></path>
                      </svg>
                    </a>

                    <!-- Dropdown Menu -->
                    <div
                      v-if="link.subLinks && link.subLinks.length > 0"
                      class="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10"
                    >
                      <div class="py-2">
                        <a
                          v-for="(subLink, subIndex) in link.subLinks"
                          :key="subIndex"
                          :href="subLink.url || '#'"
                          class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                          :style="{ fontFamily: form.fontFamily }"
                        >
                          {{ subLink.text || `Sub Link ${subIndex + 1}` }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            <!-- Action Buttons -->
            <div
              class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200"
            >
              <div class="text-sm text-gray-500">
                Preview updates in real-time as you make changes
              </div>
              <div class="flex space-x-3">
                <button
                  @click="deleteNavbar"
                  class="px-4 py-2 bg-red-100 text-red-700 font-medium rounded-lg hover:bg-red-200 transition-colors duration-200 flex items-center space-x-2"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    ></path>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Institutelayout>
</template>

<script setup>
import { ref } from "vue";
import Institutelayout from "../institutelayout.vue";

const form = ref({
  logo: null,
  logoName: "",
  logoUrl: "",
  navbarBgColor: "#ffffff",
  fontFamily: "sans-serif",
  textSize: "text-base",
  textColor: "#374151",
  logoPosition: "left",
  navLinks: [],
});

const handleLogoUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    form.value.logo = file;
    form.value.logoName = file.name;

    // Create preview URL for the logo
    const reader = new FileReader();
    reader.onload = (e) => {
      form.value.logoUrl = e.target.result;
    };
    reader.readAsDataURL(file);
  } else {
    form.value.logo = null;
    form.value.logoName = "";
    form.value.logoUrl = "";
  }
};

const addNavLink = () => {
  form.value.navLinks.push({
    text: "",
    url: "",
    subLinks: [],
    showSubLinks: false,
  });
};

const removeNavLink = (index) => {
  form.value.navLinks.splice(index, 1);
};

const toggleSubLinks = (index) => {
  form.value.navLinks[index].showSubLinks =
    !form.value.navLinks[index].showSubLinks;
};

const addSubLink = (parentIndex) => {
  if (!form.value.navLinks[parentIndex].subLinks) {
    form.value.navLinks[parentIndex].subLinks = [];
  }
  form.value.navLinks[parentIndex].subLinks.push({ text: "", url: "" });
};

const removeSubLink = (parentIndex, subIndex) => {
  form.value.navLinks[parentIndex].subLinks.splice(subIndex, 1);
};

const saveChanges = () => {
  console.log("Navbar Data:", {
    ...form.value,
    logo: form.value.logo ? form.value.logo.name : null,
  });
  alert("Changes saved successfully! Check console for the submitted data.");
};

const editNavbar = () => {
  alert(
    "Edit functionality - This would allow you to modify the current navbar configuration."
  );
};

const deleteNavbar = () => {
  if (
    confirm(
      "Are you sure you want to delete this navbar? This action cannot be undone."
    )
  ) {
    // Reset form
    form.value = {
      logo: null,
      logoName: "",
      logoUrl: "",
      navbarBgColor: "#ffffff",
      fontFamily: "sans-serif",
      textSize: "text-base",
      textColor: "#374151",
      logoPosition: "left",
      navLinks: [],
    };
    alert("Navbar deleted successfully!");
  }
};
</script>
